/**
 * Wishlist functionality using localStorage
 * Stores product handles and IDs for persistence
 */

class Wishlist {
  constructor() {
    this.storageKey = 'pokochaj_wishlist';
    this.wishlist = this.getWishlist();
    this.init();
  }

  init() {
    this.updateAllCounts();
    this.bindEvents();
    this.updateAllButtons();
  }

  /**
   * Get wishlist from localStorage
   */
  getWishlist() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error reading wishlist:', e);
      return [];
    }
  }

  /**
   * Save wishlist to localStorage
   */
  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.updateAllCounts();
      this.updateAllButtons();
      this.dispatchEvent('wishlist:updated', { wishlist: this.wishlist });
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  /**
   * Check if product is in wishlist
   */
  isInWishlist(productId) {
    return this.wishlist.some(item => item.id === productId);
  }

  /**
   * Add product to wishlist
   */
  addToWishlist(productData) {
    if (!this.isInWishlist(productData.id)) {
      this.wishlist.push({
        id: productData.id,
        handle: productData.handle,
        title: productData.title,
        description: productData.description,
        price: productData.price,
        comparePrice: productData.comparePrice,
        image: productData.image,
        url: productData.url,
        addedAt: Date.now()
      });
      this.saveWishlist();
      this.showNotification('Dodano do ulubionych');
      return true;
    }
    return false;
  }

  /**
   * Remove product from wishlist
   */
  removeFromWishlist(productId) {
    const index = this.wishlist.findIndex(item => item.id === productId);
    if (index > -1) {
      this.wishlist.splice(index, 1);
      this.saveWishlist();
      this.showNotification('Usunięto z ulubionych');
      return true;
    }
    return false;
  }

  /**
   * Toggle product in wishlist
   */
  toggleWishlist(productData) {
    if (this.isInWishlist(productData.id)) {
      return this.removeFromWishlist(productData.id);
    } else {
      return this.addToWishlist(productData);
    }
  }

  /**
   * Get wishlist count
   */
  getCount() {
    return this.wishlist.length;
  }

  /**
   * Clear entire wishlist
   */
  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
    this.showNotification('Lista ulubionych została wyczyszczona');
  }

  /**
   * Update all wishlist count badges
   */
  updateAllCounts() {
    const count = this.getCount();
    const countElements = document.querySelectorAll('[data-wishlist-count]');

    countElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /**
   * Update all wishlist button states
   */
  updateAllButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');

    buttons.forEach(btn => {
      const productId = parseInt(btn.dataset.productId, 10);
      const isActive = this.isInWishlist(productId);

      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive);

      // Update icon if exists
      const icon = btn.querySelector('.wishlist-icon');
      if (icon) {
        icon.classList.toggle('is-filled', isActive);
      }
    });
  }

  /**
   * Bind click events
   */
  bindEvents() {
    // Toggle wishlist button clicks
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();

        const productData = {
          id: parseInt(btn.dataset.productId, 10),
          handle: btn.dataset.productHandle,
          title: btn.dataset.productTitle,
          description: btn.dataset.productDescription || null,
          price: btn.dataset.productPrice,
          comparePrice: btn.dataset.productComparePrice || null,
          image: btn.dataset.productImage,
          url: btn.dataset.productUrl
        };

        this.toggleWishlist(productData);
      }
    });

    // Clear wishlist button
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-wishlist-clear]')) {
        e.preventDefault();
        if (confirm('Czy na pewno chcesz wyczyścić listę ulubionych?')) {
          this.clearWishlist();
        }
      }
    });

    // Remove single item from wishlist page
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        e.preventDefault();
        const productId = parseInt(removeBtn.dataset.productId, 10);
        this.removeFromWishlist(productId);

        // Remove item from DOM with animation
        const item = removeBtn.closest('.wishlist-item');
        if (item) {
          item.classList.add('is-removing');
          setTimeout(() => {
            item.remove();
            this.checkEmptyState();
          }, 300);
        }
      }
    });
  }

  /**
   * Check and show empty state on wishlist page
   */
  checkEmptyState() {
    const container = document.querySelector('[data-wishlist-items]');
    const emptyState = document.querySelector('[data-wishlist-empty]');

    if (container && emptyState) {
      if (this.getCount() === 0) {
        container.style.display = 'none';
        emptyState.style.display = 'block';
      }
    }
  }

  /**
   * Show notification toast
   */
  showNotification(message) {
    // Remove existing notification
    const existing = document.querySelector('.wishlist-toast');
    if (existing) {
      existing.remove();
    }

    // Create notification
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.innerHTML = `
      <div class="wishlist-toast__content">
        <svg class="wishlist-toast__icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 21.3428L2.53719 12.7713C-2.60567 7.62847 4.95433 -2.24582 12 5.74275C19.0458 -2.24582 26.5715 7.66275 21.4629 12.7713L12 21.3428Z" fill="#897455" stroke="#897455"/>
        </svg>
        <span class="wishlist-toast__text">${message}</span>
      </div>
    `;

    document.body.appendChild(toast);

    // Show toast
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Hide and remove
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  /**
   * Dispatch custom event
   */
  dispatchEvent(name, detail = {}) {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }

  /**
   * Render wishlist items on wishlist page
   */
  renderWishlistPage() {
    const container = document.querySelector('[data-wishlist-items]');
    const emptyState = document.querySelector('[data-wishlist-empty]');

    if (!container) return;

    if (this.wishlist.length === 0) {
      container.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    container.style.display = '';

    container.innerHTML = this.wishlist.map(item => `
      <div class="wishlist-item" data-product-id="${item.id}">
        <button type="button" class="wishlist-item__remove" data-wishlist-remove data-product-id="${item.id}" aria-label="Usuń z ulubionych">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="wishlist-item__image-wrapper">
          <a href="${item.url}" class="wishlist-item__image">
            <img src="${item.image}" alt="${item.title}" loading="lazy">
          </a>
          <div class="wishlist-item__overlay">
            <div class="wishlist-item__overlay-content">
              <h3 class="wishlist-item__overlay-title">${item.title}</h3>
              ${item.description ? `<p class="wishlist-item__overlay-description">${item.description}</p>` : ''}
              <a href="${item.url}" class="wishlist-item__view-btn--overlay">
                <span class="button-text">Zobacz produkt</span>
                <span class="button-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 24C11.6526 12.6314 11.3686 12.3492 0 12C11.3686 11.6526 11.6508 11.3686 12 0C12.3474 11.3686 12.6314 11.6508 24 12C12.6314 12.3492 12.3492 12.628 12 24Z" fill="currentColor"/>
                  </svg>
                </span>
              </a>
            </div>
          </div>
        </div>
        <div class="wishlist-item__info">
          <div class="wishlist-item__info-wrapper">
            <h3 class="wishlist-item__title">
              <a href="${item.url}">${item.title}</a>
            </h3>
            <div class="wishlist-item__price">
              ${item.comparePrice ? `<span class="wishlist-item__compare-price">${item.comparePrice}</span>` : ''}
              <span class="wishlist-item__current-price">${item.price}</span>
            </div>
          </div>
        </div>
      </div>
    `).join('');
  }
}

// Initialize wishlist
const wishlist = new Wishlist();

// Expose to global scope
window.Wishlist = wishlist;

// Render wishlist page on load if container exists
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('[data-wishlist-items]')) {
    wishlist.renderWishlistPage();
  }
});
